import React, { useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import StudentSearchService from "../../_services/StudentSearchService";

interface BulkMessageModalProps {
  show: boolean;
  onHide: () => void;
  channel: string; // "sms" | "email" | "whatsapp" | "notification"
  selectedEnquiryIds: number[];
  lookups: any;
}

const BulkMessageModal: React.FC<BulkMessageModalProps> = ({
  show,
  onHide,
  channel,
  selectedEnquiryIds,
  lookups,
}) => {
  const [templateId, setTemplateId] = useState<string>("");
  const [customMessage, setCustomMessage] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [sendTo, setSendTo] = useState<string[]>(["student"]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<any>(null);

  const channelLabel =
    channel === "sms" ? "SMS" :
    channel === "email" ? "Email" :
    channel === "whatsapp" ? "WhatsApp" :
    "Notification";

  const templates =
    channel === "sms" ? (lookups?.smsTemplates || []) :
    channel === "email" ? (lookups?.emailTemplates || []) :
    (lookups?.notificationTemplates || []);

  const toggleSendTo = (target: string) => {
    setSendTo((prev) =>
      prev.includes(target)
        ? prev.filter((t) => t !== target)
        : [...prev, target],
    );
  };

  const handleSend = async () => {
    if (selectedEnquiryIds.length === 0) return;
    setSending(true);
    setResult(null);
    try {
      const resp = await StudentSearchService.sendBulkMessage({
        enquiryIds: selectedEnquiryIds,
        channel,
        templateId: templateId ? Number(templateId) : undefined,
        customMessage: customMessage || undefined,
        customSubject: customSubject || undefined,
        sendTo,
      });
      setResult(resp.data?.data || { sent: 0 });
    } catch (err: any) {
      setResult({ error: err?.response?.data?.message || "Failed to send" });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setCustomMessage("");
    setCustomSubject("");
    setTemplateId("");
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          Send {channelLabel}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 4,
              marginBottom: 10,
              background: result.error ? "#fef2f2" : "#f0fdf4",
              color: result.error ? "#b91c1c" : "#15803d",
              fontSize: 13,
            }}
          >
            {result.error
              ? result.error
              : `Sent: ${result.sent || 0}, Failed: ${result.failed || 0}`}
          </div>
        )}

        <Row className="mb-2">
          <Col md={3} style={{ display: "flex", alignItems: "center" }}>To</Col>
          <Col md={9}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              {selectedEnquiryIds.length} student(s) selected
            </span>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md={3} style={{ display: "flex", alignItems: "center" }}>Send To</Col>
          <Col md={9}>
            {["student", "alternate", "father", "mother", "guardian"].map((t) => (
              <Form.Check
                key={t}
                inline
                type="checkbox"
                label={t.charAt(0).toUpperCase() + t.slice(1)}
                checked={sendTo.includes(t)}
                onChange={() => toggleSendTo(t)}
                style={{ fontSize: 13 }}
              />
            ))}
          </Col>
        </Row>

        {channel === "email" && (
          <Row className="mb-2">
            <Col md={3} style={{ display: "flex", alignItems: "center" }}>Subject</Col>
            <Col md={9}>
              <Form.Control size="sm" type="text"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
              />
            </Col>
          </Row>
        )}

        <Row className="mb-2">
          <Col md={3} style={{ display: "flex", alignItems: "center" }}>Template</Col>
          <Col md={9}>
            <Form.Select size="sm" value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              <option value="">#SELECT#</option>
              {templates.map((t: any) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md={3} style={{ display: "flex", alignItems: "center" }}>
            Message
          </Col>
          <Col md={9}>
            <Form.Control
              as="textarea"
              rows={5}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              style={{ fontSize: 13 }}
            />
            <div style={{ textAlign: "right", fontSize: 11, color: "var(--text-faint)" }}>
              Approx. {customMessage.length} characters
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSend}
          disabled={sending || selectedEnquiryIds.length === 0}
        >
          {sending ? "Sending..." : `Send ${channelLabel}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkMessageModal;
